/**
 * resolveKeviaAction.js
 *
 * Deterministic, frontend-only action resolver.
 * Detects navigation intent in user messages and returns
 * contextual action chips to render below Kevia's response.
 *
 * Design rules:
 *  - No LLM involved — fast, offline-capable
 *  - Max 3 chips returned to avoid cluttering the UI
 *  - High-signal nouns fire without a trigger verb
 *  - Generic pastoral nouns (familias, visitas) require a trigger verb
 */

/* ─── Accent normalization ────────────────────────────────────────────── */

function normalizeText(text) {
  return text.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase();
}

/* ─── Navigation trigger verbs ────────────────────────────────────────── */

const NAV_TRIGGER = /\b(ver|muestrame|mostrarme|mostrar|abri[rt]|llevar|ir\s+a[l]?|navegar?|acceder?|accede|abre|muestra|dame|ensenme|quiero\s+ver|necesito\s+ver|llevame)\b/i;

/* ─── Module definitions (matched against normalized text) ───────────── */

const MODULE_DEFS = [
  // --- High-signal: match with or without trigger verb ---
  {
    highSignal: true,
    patterns: [/transferencia(s)?/, /\btransfer\b/],
    actions: [{ type: "navigate", target: "/admin/operations", label: "Ver transferencias" }],
  },
  {
    highSignal: true,
    patterns: [/auditoria/, /registro\s+de\s+actividad/, /historial\s+de\s+eventos/, /movimientos\s+sospechosos/, /actividad\s+reciente\s+del\s+sistema/],
    actions: [{ type: "navigate", target: "/admin/audit", label: "Ver auditoría" }],
  },
  {
    highSignal: true,
    patterns: [/operaciones?\s*(distritales?|del\s+distrito)?/, /resumen\s+operativo/, /salud\s+del\s+distrito/, /integridad\s+(de\s+)?(datos?|sistema)/, /problemas?\s+de\s+integridad/],
    actions: [{ type: "navigate", target: "/admin/operations", label: "Ver operaciones" }],
  },
  {
    highSignal: true,
    patterns: [/alertas?\s+(activas?|pastorales?)/, /alertas?\s+del\s+distrito/],
    actions: [{ type: "navigate", target: "/admin/operations", label: "Ver alertas" }],
  },
  {
    highSignal: true,
    patterns: [/junta\s+directiva/, /\borganigrama\b/, /\bboard\b/, /liderazgo\s+del\s+distrito/],
    actions: [{ type: "navigate", target: "/churches", label: "Ver iglesias" }],
  },
  {
    highSignal: true,
    patterns: [/revision\s+mensual/, /checklist\s+(mensual|pastoral)/],
    actions: [{ type: "navigate", target: "/administrative-review", label: "Ver revisión mensual" }],
  },

  // --- Require trigger verb ---
  {
    patterns: [/\biglesias?\b/, /\bcongregaciones?\b/],
    actions: [{ type: "navigate", target: "/churches", label: "Ver iglesias" }],
  },
  {
    patterns: [/\bfamilias?\b/, /\bhogares?\b/],
    actions: [{ type: "navigate", target: "/families", label: "Ver familias" }],
  },
  {
    patterns: [/\bpersonas?\b/, /\bmiembros?\b/, /\bfeligreses?\b/],
    actions: [{ type: "navigate", target: "/families", label: "Ver personas" }],
  },
  {
    patterns: [/\bvisitas?\b/],
    actions: [{ type: "navigate", target: "/visits", label: "Ver visitas" }],
  },
  {
    patterns: [/\boraciones?\b/],
    actions: [{ type: "navigate", target: "/prayers", label: "Ver oraciones" }],
  },
  {
    highSignal: true,
    patterns: [/\beventos?\b/, /\bactividades?\b/],
    actions: [{ type: "navigate", target: "/agenda", label: "Ver eventos" }],
  },
  {
    patterns: [/\bsermones?\b/, /\bpredicaciones?\b/, /\bpredicar?\b/],
    actions: [{ type: "navigate", target: "/preaching", label: "Ver predicaciones" }],
  },
  {
    patterns: [/\bmateriales?\b/, /\bfinanzas?\b/, /\bpresupuestos?\b/],
    actions: [{ type: "navigate", target: "/materials", label: "Ver materiales" }],
  },
  {
    patterns: [/\busuarios?\s*(del\s+sistema)?\b/, /\baccesos?\s+al\s+sistema/],
    actions: [{ type: "navigate", target: "/admin/users", label: "Ver usuarios" }],
  },
  {
    patterns: [/\bnotas?\b/],
    actions: [{ type: "navigate", target: "/notes", label: "Ver notas" }],
  },
];

/* ─── Compound multi-step patterns ───────────────────────────────────── */

// Patterns that produce multiple chips simultaneously (e.g. "¿quién necesita seguimiento?" → families + visits)
const COMPOUND_DEFS = [
  {
    patterns: [/familias?\s+que\s+necesitan?\s+seguimiento/, /seguimiento\s+(de\s+)?familias?/, /familias?\s+sin\s+visita/],
    actions: [
      { type: "navigate", target: "/families", label: "Ver familias" },
      { type: "navigate", target: "/visits",   label: "Ver visitas"  },
    ],
  },
  {
    patterns: [/personas?\s+que\s+necesitan?\s+atenci[oó]n/, /miembros?\s+(sin\s+seguimiento|inactivos?)/, /qui[eé]n\s+necesita\s+visita/],
    actions: [
      { type: "navigate", target: "/families", label: "Ver personas" },
      { type: "navigate", target: "/visits",  label: "Ver visitas"  },
    ],
  },
  {
    patterns: [/estado\s+(del\s+)?distrito/, /panorama\s+(pastoral|distrital)/, /resumen\s+del\s+distrito/],
    actions: [
      { type: "navigate", target: "/admin/operations", label: "Ver operaciones" },
      { type: "navigate", target: "/churches",          label: "Ver iglesias"    },
    ],
  },
];

/* ─── Public API ──────────────────────────────────────────────────────── */

/**
 * @param {string} text — the user's message
 * @returns {{ type: string, target: string, label: string, filter?: string }[]}
 */
export function resolveKeviaAction(text) {
  if (!text?.trim()) return [];

  // Normalize accents so "auditoría" and "auditoria" both match
  const norm          = normalizeText(text);
  const hasNavTrigger = NAV_TRIGGER.test(norm);
  const results       = [];
  const seen          = new Set();

  // Compound patterns take priority (they produce 2 chips intentionally)
  for (const def of COMPOUND_DEFS) {
    if (def.patterns.some((p) => p.test(norm))) {
      for (const action of def.actions) {
        const key = action.target + (action.filter ?? "");
        if (!seen.has(key)) { seen.add(key); results.push(action); }
      }
      if (results.length >= 3) return results;
      break; // only first compound match
    }
  }

  if (results.length >= 3) return results;

  for (const def of MODULE_DEFS) {
    if (!def.highSignal && !hasNavTrigger) continue;

    const matches = def.patterns.some((p) => p.test(norm));
    if (!matches) continue;

    for (const action of def.actions) {
      const key = action.target + (action.filter ?? "");
      if (seen.has(key)) continue;
      seen.add(key);
      results.push(action);
    }

    if (results.length >= 3) break;
  }

  return results;
}
